import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/* CSS */
import shared from '../styles/shared.module.css';
import styles from "./CheckEmailPage.module.css";

/* Components */
import AuthHeader from '../components/layout/AuthHeader';

/* API helper */
import { api } from "../api";

/* Icons */
import exclamation_blue from '../assets/icons/exclamation_blue.svg';
import exclamation_red from '../assets/icons/exclamation_red.svg';
import arrow_back from '../assets/icons/arrow_back.svg';

const CheckEmailPage = () => {

    const navigate = useNavigate();
    const location = useLocation();

    const [code, setCode] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    // Unpack the email! ("" as a safe fallback just in case user refreshed the page)
    const email = location.state?.email || "";
    const next = location.state?.next || null;

    // Which flow are we in? "signup" (default) or "reset" — this one screen serves both.
    const mode = location.state?.mode || 'signup';

    // Everything that differs between signup and reset lives in this lookup.
    const config = {
        signup: {
            title: 'Verify your email address',
            verifyEndpoint: '/auth/verify-code',
            resendEndpoint: '/auth/check-email',
            nextRoute: '/passwordsetup',
        },
        reset: {
            title: 'Check your email',
            verifyEndpoint: '/auth/verify-reset-code',
            resendEndpoint: '/auth/forgot-password',
            nextRoute: '/resetpassword',
        },
    };
    const { title, verifyEndpoint, resendEndpoint, nextRoute } = config[mode];

    // Timer starts at 59
    const [timer, setTimer] = useState(59);

    // The countdown effect
    useEffect(() => {
        // If time hits 0, stop counting
        if (timer === 0) return;

        // Set an interval to subtract 1 every second
        const timerId = setInterval(() => {
            setTimer((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(timerId);
    }, [timer]);

    // Format the time as 00:XX
    const formatTime = (seconds) => {
        const secs = seconds < 10 ? `0${seconds}` : seconds;
        return `00:${secs}`
    }

    const handleVerify = async () => {
        // Prevent double-submissions
        if (isLoading) return;

        try {
            setIsLoading(true);
            await api.post(verifyEndpoint, { email, code });
            // Pass the code forward too — the reset page re-submits it as the real security gate
            navigate(nextRoute, { state: { email, code, next } });
        } catch (err) {
            console.error("Error verifying code:", err);
            setError(err.message || "Server error. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            <AuthHeader />

            <div className={shared.authBody}>
                <div className={shared.authTitleWrapper}>
                    <button
                        className={shared.backButton}
                        onClick={() => window.history.back()}
                    >
                        <img
                            src={arrow_back}
                            alt="Back to previous page"
                            className={shared.backArrow}
                        />
                        Back
                    </button>
                </div>

                {/* Main title */}
                <div className={styles.title}>
                    {title}
                </div>

                <div className={styles.contentContainer}>
                    {/* Message */}
                    <div className={styles.messageWrapper}>
                        <img className={styles.messageIcon}
                            src={exclamation_blue}
                            alt="Exclamation icon"
                        />
                        <div className={styles.messageContext}>
                            Enter the 6-digit code we sent to&nbsp;<span style={{ fontWeight: '700' }}>{email}</span>. <br />The email can take up to&nbsp;<span style={{ fontWeight: '700' }}>1 minute</span>&nbsp;to arrive.
                        </div>
                    </div>

                    {/* Enter verification code */}
                    <div className={shared.authField}>
                        <div className={shared.authFieldLabel} style={{ fontWeight: '600' }}>Enter verification code</div>
                        <input
                            type="text"
                            className={shared.codeInput}
                            maxLength="6"
                            value={code}
                            onChange={(e) => {
                                // Strip out any character that is NOT 0-9
                                const numbersOnly = e.target.value.replace(/[^0-9]/g, '');
                                setCode(numbersOnly);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleVerify();
                                }
                            }}
                            placeholder="------"
                            autoFocus
                        />
                        {/* Inline Error Message */}
                        {error && (
                            <div className={styles.error}>
                                <img className={styles.errorIcon}
                                    src={exclamation_red}
                                    alt="Exclamation icon"
                                />
                                <div className={styles.errorMessage}>{error}</div>
                            </div>
                        )}
                    </div>

                    {/* Verify code button */}
                    <button
                        className={`${styles.verifyButton} ${isLoading ? shared.buttonLoading : ''}`}
                        onClick={handleVerify}
                    >
                        <div className={shared.buttonContent}>
                            {isLoading ? "Verifying..." : "Verify Code"}
                            {isLoading && <span className={shared.spinner}></span>}
                        </div>
                    </button>

                    {/* Resend email button */}
                    <button
                        className={styles.resendButton}
                        disabled={timer > 0}
                        onClick={async () => {
                            if (timer === 0) {
                                setTimer(59);
                                // Ask the server to send a new email
                                try {
                                    await api.post(resendEndpoint, { email });
                                } catch (err) {
                                    console.error("Error resending email:", err);
                                }
                            }
                        }}
                    >
                        {/* Conditionally render the text */}
                        {timer > 0 ? `Resend email (${formatTime(timer)})` : "Resend email"}
                    </button>
                </div>
            </div>
        </>
    );
};

export default CheckEmailPage;